import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {Box, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {
  useDeleteQuickPresetMutation,
  useMoveDownQuickPresetMutation,
  useMoveUpQuickPresetMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  formatDocumentTitle,
  handleApiError,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function AdvertisingQuickPresets() {
  const navigate = useNavigate();

  const [deleteQuickPreset] = useDeleteQuickPresetMutation();
  const [moveUpQuickPreset] = useMoveUpQuickPresetMutation();
  const [moveDownQuickPreset] = useMoveDownQuickPresetMutation();

  const handleActionsCallback: ActionCallback = (action) => {
    const rowId = Array.isArray(action.rowId) ? action.rowId[0] : action.rowId;

    match(action.actionKey)
      .with('detail', 'edit', () =>
        navigate(composePath(settingsRoutes.advertisingQuickPresetsDetail, {params: {id: rowId}}))
      )
      .with('move_up', () => {
        moveUpQuickPreset({id: rowId})
          .unwrap()
          .then(() => {
            action.refreshData();
          })
          .catch(handleApiError);
      })
      .with('move_down', () => {
        moveDownQuickPreset({id: rowId})
          .unwrap()
          .then(() => {
            action.refreshData();
          })
          .catch(handleApiError);
      })
      .with('delete', () => {
        openDeleteDialog({
          text: i18n.t('general.actions.confirmNeutralQuestion'),
          onConfirm: () => {
            deleteQuickPreset({id: rowId})
              .unwrap()
              .then(() => {
                action.refreshData();
              })
              .catch(handleApiError);
          },
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(action.actionKey));
  };

  return (
    <>
      <Helmet
        title={formatDocumentTitle(
          i18n.t('page.settings.labels.advertisingQuickPresets.title'),
          i18n.t('page.settings.labels.advertisementPlatform'),
          i18n.t('page.salesSettings.labels.advertising'),
          i18n.t('page.settings.labels.title')
        )}
      />
      <SettingsTemplate
        header={{
          title: i18n.t('page.settings.labels.advertisingQuickPresets.title'),
          breadcrumbs: [
            {
              label: i18n.t('page.salesSettings.labels.advertising'),
              href: settingsRoutes.advertising,
            },
            {
              label: i18n.t('page.settings.labels.advertisementPlatform'),
              href: settingsRoutes.advertisingPlatforms,
            },
          ],
          actions: [
            {
              type: 'button',
              title: i18n.t('page.settings.labels.advertisingQuickPresets.create'),
              to: settingsRoutes.advertisingQuickPresetsCreate,
            },
          ],
        }}
        description={i18n.t('page.settings.labels.advertisingQuickPresets.description')}
        data-testid={testIds.settings.advertisingQuickPresets()}
      >
        <VStack spacing={6} height="100%">
          <Box height="100%">
            <DmsDataGrid
              data-testid={testIds.settings.advertisingQuickPresets('grid')}
              gridCode="advertising-quick-preset"
              actionCallback={handleActionsCallback}
              autoHeight
            />
          </Box>
        </VStack>
      </SettingsTemplate>
    </>
  );
}
